export type RangeFilterKey = 'week' | 'month' | 'quarter' | 'year' | 'max'

export type RangeFilterConfig = {
   key: RangeFilterKey
   labelKey: string
   days: number | null
}

const MS_IN_DAY = 24 * 60 * 60 * 1000
export const DEFAULT_RANGE_FILTER_KEY: RangeFilterKey = 'quarter'
export const RANGE_FILTER_KEYS: readonly RangeFilterKey[] = [
   'week',
   'month',
   'quarter',
   'year',
   'max',
]

export const RANGE_FILTER_CONFIG: Record<RangeFilterKey, RangeFilterConfig> = {
   week: { key: 'week', labelKey: 'Week', days: 7 },
   month: { key: 'month', labelKey: 'Month', days: 30 },
   quarter: { key: 'quarter', labelKey: '3 Months', days: 90 },
   year: { key: 'year', labelKey: 'Year', days: 365 },
   max: { key: 'max', labelKey: 'Max', days: null },
}

/**
 * Returns the config for a range filter key, falling back to the default.
 *
 * @param key - The filter key.
 * @returns The filter config.
 */
export function getRangeFilterConfig(key: RangeFilterKey): RangeFilterConfig {
   return RANGE_FILTER_CONFIG[key] || RANGE_FILTER_CONFIG[DEFAULT_RANGE_FILTER_KEY]
}

/**
 * Computes the start/end of a time range: the last N days for fixed filters, or the
 * span of all timestamps for `max`. The end extends to the latest timestamp when it
 * is later than now, so an entry logged slightly ahead of the clock stays visible.
 *
 * @param filterKey - The filter key.
 * @param timestamps - Every known timestamp (epoch ms), used for `max` and the end.
 * @returns The range start and end (epoch ms).
 */
export function getRangeTimestamps(filterKey: RangeFilterKey, timestamps: number[]) {
   const filter = getRangeFilterConfig(filterKey)
   const now = Date.now()
   const finiteTimestamps = timestamps.filter(timestamp => Number.isFinite(timestamp))
   const rangeEnd = Math.max(now, ...finiteTimestamps)

   let rangeStart: number | null =
      filter.days !== null ? now - filter.days * MS_IN_DAY : null
   if (filter.key === 'max' && finiteTimestamps.length > 0) {
      rangeStart = Math.min(...finiteTimestamps)
   }
   if (rangeStart === null || !Number.isFinite(rangeStart)) {
      rangeStart = now - 90 * MS_IN_DAY
   }
   if (rangeStart >= rangeEnd) {
      rangeStart = rangeEnd - MS_IN_DAY
   }

   return { rangeStart, rangeEnd }
}
